import React, { useState, useEffect } from 'react';
import { X, User, Lock, Mail, Eye, EyeOff, Key } from 'lucide-react';
import { toast } from 'sonner';

const SetCredentialsModal = ({ isOpen, onClose, customer, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [autoGenerate, setAutoGenerate] = useState(true);

  useEffect(() => {
    if (customer) {
      // Generate simple username from name
      const firstName = customer.firstName || '';
      const suggestedUsername = firstName.toLowerCase().replace(/\s/g, '') + Math.floor(Math.random() * 999);
      
      setFormData({
        username: customer.username || suggestedUsername,
        email: customer.email || '',
        password: '',
        confirmPassword: ''
      });
    }
  }, [customer]);

  // Auto-generate password when modal opens if autoGenerate is true
  useEffect(() => {
    if (customer && autoGenerate && !formData.password) {
      generatePassword();
    }
  }, [customer, autoGenerate]);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const specialChars = '@#$%';
    let password = 'Netix';
    
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    password += specialChars.charAt(Math.floor(Math.random() * specialChars.length));
    password += Math.floor(Math.random() * 100);
    
    setFormData(prev => ({
      ...prev,
      password: password,
      confirmPassword: password
    }));
    
    toast.success('Password generated!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters!');
      return;
    }

    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('customerAdminToken');

      // Update customer credentials
      const updateResponse = await fetch(`${backendUrl}/api/customer-admin/customers/${customer._id}/update-credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          sendEmail: sendEmail
        })
      });

      // Parse response first
      let data;
      try {
        data = await updateResponse.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        if (updateResponse.ok) {
          // If response was OK but couldn't parse, assume success
          toast.success(`✅ Credentials updated successfully!`);
          onSuccess();
          onClose();
          setFormData({
            email: '',
            password: '',
            confirmPassword: ''
          });
          return;
        } else {
          toast.error('Server error. Please try again.');
          return;
        }
      }

      // Check response status
      if (!updateResponse.ok) {
        toast.error(data.detail || 'Failed to update credentials');
        return;
      }

      // Success! Show appropriate message and close after a short delay
      if (data.emailSent) {
        toast.success(`✅ Credentials updated and email sent to ${formData.email}`, {
          duration: 3000
        });
      } else if (sendEmail) {
        // Email was requested but couldn't be sent
        toast.warning(`⚠️ Credentials updated but email could not be sent. Please check SendGrid configuration.`, {
          duration: 4000
        });
      } else {
        toast.success('✅ Credentials updated successfully!', {
          duration: 3000
        });
      }
      
      // Call success callback and close modal after showing message
      onSuccess();
      
      // Delay closing to ensure toast is visible
      setTimeout(() => {
        onClose();
        // Reset form
        setFormData({
          email: '',
          password: '',
          confirmPassword: ''
        });
      }, 500);
      
    } catch (error) {
      console.error('Error updating credentials:', error);
      // More specific error message
      if (error.message && error.message.includes('fetch')) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 flex justify-between items-center rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            <div>
              <h2 className="text-xl font-bold">Set Login Credentials</h2>
              <p className="text-xs text-purple-100">
                {customer?.firstName} {customer?.lastName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 rounded-full p-1 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Current Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h3 className="font-semibold text-blue-900 text-sm mb-2 flex items-center gap-1">
              <User className="h-3 w-3" />
              Customer Information
            </h3>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-600">Name:</span>
                <span className="ml-1 font-medium">{customer?.firstName} {customer?.lastName}</span>
              </div>
              <div>
                <span className="text-gray-600">Account ID:</span>
                <span className="ml-1 font-medium">{customer?.accountId}</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-600">Company:</span>
                <span className="ml-1 font-medium">{customer?.companyName || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Username Field (Simple & Easy to Remember) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <User className="inline h-3 w-3 mr-1" />
              Username (Login ID) *
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              placeholder="e.g., pravin123, customer001"
              className="w-full px-3 py-2 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              required
            />
            <p className="text-xs text-emerald-600 mt-1 font-medium">
              ✓ Simple & easy to remember username for customer login
            </p>
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Mail className="inline h-3 w-3 mr-1" />
              Customer Email
            </label>
            <input
              type="email"
              value={formData.email}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 text-sm cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">
              Email for notifications and password reset
            </p>
          </div>

          {/* Auto-Generate Password Checkbox */}
          <div className="flex items-center gap-2 bg-purple-50 p-3 rounded-lg border border-purple-200">
            <input
              type="checkbox"
              id="autoGenerate"
              checked={autoGenerate}
              onChange={(e) => setAutoGenerate(e.target.checked)}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <label htmlFor="autoGenerate" className="text-sm text-gray-700 font-medium cursor-pointer">
              Auto-generate secure password
            </label>
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Lock className="inline h-3 w-3 mr-1" />
              Password (Set by Admin) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-24 text-sm"
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-14 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={generatePassword}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700"
                title="Generate Password"
              >
                Gen
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Password must be at least 8 characters. Click "Gen" to auto-generate.
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Lock className="inline h-3 w-3 mr-1" />
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              placeholder="Re-enter password"
            />
          </div>

          {/* Send Email Checkbox */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
              />
              <div>
                <span className="font-medium text-green-900 text-sm">Send credentials via email</span>
                <p className="text-xs text-green-700">Email sent from update@netixfibernet.com</p>
              </div>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition font-semibold disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Updating...
                </>
              ) : (
                <>
                  <Key className="h-4 w-4" />
                  {sendEmail ? 'Update & Send' : 'Update'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetCredentialsModal;
